import _classCallCheck from "/home/runner/work/soroban-rpc-api/soroban-rpc-api/build/docs/gatsby/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/home/runner/work/soroban-rpc-api/soroban-rpc-api/build/docs/gatsby/node_modules/@babel/runtime/helpers/esm/createClass";

/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import './list.css';
import { Disposable } from '../../../common/lifecycle.js';
import { range } from '../../../common/arrays.js';
import { List } from './listWidget.js';
import { Event } from '../../../common/event.js';
import { CancellationTokenSource } from '../../../common/cancellation.js';

var PagedRenderer = /*#__PURE__*/function () {
  function PagedRenderer(renderer, modelProvider) {
    _classCallCheck(this, PagedRenderer);

    this.renderer = renderer;
    this.modelProvider = modelProvider;
  }

  _createClass(PagedRenderer, [{
    key: "templateId",
    get: function get() {
      return this.renderer.templateId;
    }
  }, {
    key: "renderTemplate",
    value: function renderTemplate(container) {
      var data = this.renderer.renderTemplate(container);
      return {
        data: data,
        disposable: Disposable.None
      };
    }
  }, {
    key: "renderElement",
    value: function renderElement(index, _, data, height) {
      var _this = this;

      if (data.disposable) {
        data.disposable.dispose();
      }

      if (!data.data) {
        return;
      }

      var model = this.modelProvider();

      if (model.isResolved(index)) {
        return this.renderer.renderElement(model.get(index), index, data.data, height);
      }

      var cts = new CancellationTokenSource();
      var promise = model.resolve(index, cts.token);
      data.disposable = {
        dispose: function dispose() {
          return cts.cancel();
        }
      };
      this.renderer.renderPlaceholder(index, data.data);
      promise.then(function (entry) {
        return _this.renderer.renderElement(entry, index, data.data, height);
      });
    }
  }, {
    key: "disposeTemplate",
    value: function disposeTemplate(data) {
      if (data.disposable) {
        data.disposable.dispose();
        data.disposable = undefined;
      }

      if (data.data) {
        this.renderer.disposeTemplate(data.data);
        data.data = undefined;
      }
    }
  }]);

  return PagedRenderer;
}();

var PagedAccessibilityProvider = /*#__PURE__*/function () {
  function PagedAccessibilityProvider(modelProvider, accessibilityProvider) {
    _classCallCheck(this, PagedAccessibilityProvider);

    this.modelProvider = modelProvider;
    this.accessibilityProvider = accessibilityProvider;
  }

  _createClass(PagedAccessibilityProvider, [{
    key: "getWidgetAriaLabel",
    value: function getWidgetAriaLabel() {
      return this.accessibilityProvider.getWidgetAriaLabel();
    }
  }, {
    key: "getAriaLabel",
    value: function getAriaLabel(index) {
      var model = this.modelProvider();

      if (!model.isResolved(index)) {
        return null;
      }

      return this.accessibilityProvider.getAriaLabel(model.get(index));
    }
  }]);

  return PagedAccessibilityProvider;
}();

function fromPagedListOptions(modelProvider, options) {
  return Object.assign(Object.assign({}, options), {
    accessibilityProvider: options.accessibilityProvider && new PagedAccessibilityProvider(modelProvider, options.accessibilityProvider)
  });
}

export var PagedList = /*#__PURE__*/function () {
  function PagedList(user, container, virtualDelegate, renderers) {
    var _this2 = this;

    var options = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};

    _classCallCheck(this, PagedList);

    var modelProvider = function modelProvider() {
      return _this2.model;
    };

    var pagedRenderers = renderers.map(function (r) {
      return new PagedRenderer(r, modelProvider);
    });
    this.list = new List(user, container, virtualDelegate, pagedRenderers, fromPagedListOptions(modelProvider, options));
  }

  _createClass(PagedList, [{
    key: "updateOptions",
    value: function updateOptions(options) {
      this.list.updateOptions(options);
    }
  }, {
    key: "getHTMLElement",
    value: function getHTMLElement() {
      return this.list.getHTMLElement();
    }
  }, {
    key: "onDidFocus",
    get: function get() {
      return this.list.onDidFocus;
    }
  }, {
    key: "onDidDispose",
    get: function get() {
      return this.list.onDidDispose;
    }
  }, {
    key: "onMouseDblClick",
    get: function get() {
      var _this3 = this;

      return Event.map(this.list.onMouseDblClick, function (_ref) {
        var element = _ref.element,
            index = _ref.index,
            browserEvent = _ref.browserEvent;
        return {
          element: element === undefined ? undefined : _this3._model.get(element),
          index: index,
          browserEvent: browserEvent
        };
      });
    }
  }, {
    key: "onPointer",
    get: function get() {
      var _this4 = this;

      return Event.map(this.list.onPointer, function (_ref2) {
        var element = _ref2.element,
            index = _ref2.index,
            browserEvent = _ref2.browserEvent;
        return {
          element: element === undefined ? undefined : _this4._model.get(element),
          index: index,
          browserEvent: browserEvent
        };
      });
    }
  }, {
    key: "onDidChangeFocus",
    get: function get() {
      var _this5 = this;

      return Event.map(this.list.onDidChangeFocus, function (_ref3) {
        var elements = _ref3.elements,
            indexes = _ref3.indexes,
            browserEvent = _ref3.browserEvent;
        return {
          elements: elements.map(function (e) {
            return _this5._model.get(e);
          }),
          indexes: indexes,
          browserEvent: browserEvent
        };
      });
    }
  }, {
    key: "onDidChangeSelection",
    get: function get() {
      var _this6 = this;

      return Event.map(this.list.onDidChangeSelection, function (_ref4) {
        var elements = _ref4.elements,
            indexes = _ref4.indexes,
            browserEvent = _ref4.browserEvent;
        return {
          elements: elements.map(function (e) {
            return _this6._model.get(e);
          }),
          indexes: indexes,
          browserEvent: browserEvent
        };
      });
    }
  }, {
    key: "model",
    get: function get() {
      return this._model;
    },
    set: function set(model) {
      this._model = model;
      this.list.splice(0, this.list.length, range(model.length));
    }
  }, {
    key: "getFocus",
    value: function getFocus() {
      return this.list.getFocus();
    }
  }, {
    key: "setSelection",
    value: function setSelection(indexes, browserEvent) {
      this.list.setSelection(indexes, browserEvent);
    }
  }, {
    key: "getSelection",
    value: function getSelection() {
      return this.list.getSelection();
    }
  }, {
    key: "style",
    value: function style(styles) {
      this.list.style(styles);
    }
  }, {
    key: "dispose",
    value: function dispose() {
      this.list.dispose();
    }
  }]);

  return PagedList;
}();